import React, {useState} from 'react';
import styled from 'styled-components';
import {Select, Radio} from 'antd';
import type {SelectProps} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import {useDispatch, useSelector} from 'react-redux';
import {cardSelect} from '../../../entities/card';
import {AppDispatch} from '../../../config/store';
import {authInfoSelect} from '../../../entities/auth';
import {logsSelect, setTableParams} from '../../../entities/logs';
import {colors} from '../../../styles';
import {flexMixin, textMixin} from '../../../styles/mixins';
import dayjs from 'dayjs';
import {TimePicker} from 'antd';
import {RadioChangeEvent} from 'antd';

interface SelectOption {
	label: string;
	value: string;
}

interface IPropsFilterModal {
	modalVisibilityToggler: () => void;
}

export const TransactionFilterModal: React.FC<IPropsFilterModal> = ({modalVisibilityToggler}) => {
	const {tableParams} = useSelector(logsSelect);
	const {templates} = useSelector(cardSelect);
	const {shop} = useSelector(authInfoSelect);
	const {filters} = tableParams;
	const dispatch = useDispatch<AppDispatch>();

	const [gender, setGender] = useState<number | null>(() => {
		const initGender = filters?.sex?.length === 1 ? Number(filters.sex[0]) : null;
		return initGender;
	});
	const [paymentMethod, setPaymentMethod] = useState<number | null>(() => {
		const initStatus = filters?.isActive?.length === 1 ? Number(filters.isActive[0]) : null;
		return initStatus;
	});

	const [selectedValues, setSelectedValues] = useState<string[]>(() =>
		filters?.templates ? filters.templates.map(String) : [],
	);

	const [startTime, setStartTime] = useState<string | null>(null);
	const [endTime, setEndTime] = useState<string | null>(null);
	const onSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (!shop) return;
		event.preventDefault();

		dispatch(
			setTableParams({
				...tableParams,
				pagination: {
					...tableParams.pagination,
					current: 1,
				},
				filters: {
					...tableParams.filters,
					sex: gender ? [gender.toString()] : null,
					template: selectedValues.length > 0 ? selectedValues : null,
					payment: paymentMethod ? [paymentMethod.toString()] : null,
					hours: startTime && endTime ? (`${startTime},${endTime}` as any) : null,
				},
			}),
		);

		modalVisibilityToggler();
	};

	const options: SelectProps['options'] = [
		{label: 'Выбрать все', value: '0'},
		...(templates?.map((item, i) => ({
			label: item.name,
			value: item.id.toString(),
		})) || []),
	];

	const handleChange = (values: string[]) => {
		if (values.includes('0')) {
			if (!selectedValues.includes('0')) {
				setSelectedValues([
					...options
						.filter((opt): opt is SelectOption => !!opt.value)
						.map((opt) => opt.value),
				]);
			} else {
				setSelectedValues([]);
			}
		} else {
			setSelectedValues(values);
		}
	};
	const handleStartTimeChange = (time: dayjs.Dayjs | null) => {
		setStartTime(time ? time.format('HH') : null);
	};
	const handleEndTimeChange = (time: dayjs.Dayjs | null) => {
		setEndTime(time ? time.format('HH') : null);
	};

	const handleGenderChange = (e: RadioChangeEvent) => {
		setGender(e.target.value);
	};
	const handlePaymentMethodChange = (e: RadioChangeEvent) => {
		setPaymentMethod(e.target.value);
	};

	return (
		<Wrapper>
			<HeaderTitle>
				<Title>Фильтр</Title>
				<CloseBtn onClick={modalVisibilityToggler}>Закрыть</CloseBtn>
			</HeaderTitle>
			<FormContainer>
				<WrapBoxForm>
					<FormItemsContainer>
						<span>Время начала транзакций</span>
						<FormItemContainer>
							<TimePickerStyled
								format='HH'
								onChange={handleStartTimeChange}
								value={startTime ? dayjs(startTime, 'HH') : null}
							/>
						</FormItemContainer>
						<span>Время конца транзакций</span>
						<FormItemContainer>
							<TimePickerStyled
								format='HH'
								onChange={handleEndTimeChange}
								value={endTime ? dayjs(endTime, 'HH') : null}
							/>
						</FormItemContainer>

						<span>По полу</span>
						<FormItemContainer>
							<FormItem>
								<Radio.Group onChange={handleGenderChange} value={gender}>
									<Radio value={1}>Мужской</Radio>
									<Radio value={2}>Женский</Radio>
								</Radio.Group>
							</FormItem>
							{gender !== null && (
								<DeleteOutlined
									style={{
										fontSize: '20px',
										color: colors.primaryBg,
									}}
									onClick={() => {
										setGender(null);
									}}
								/>
							)}
						</FormItemContainer>

						<span>По макету</span>
						<FormItemContainer>
							<FormItem>
								<WrapSelect>
									<Select
										mode='multiple'
										allowClear
										style={{width: '100%'}}
										maxTagCount={4}
										placeholder='Выбрать макеты'
										value={selectedValues}
										onChange={handleChange}
										options={options}
									/>
								</WrapSelect>
							</FormItem>
							{selectedValues.length > 0 && (
								<DeleteOutlined
									style={{
										fontSize: '20px',
										color: colors.primaryBg,
									}}
									onClick={() => {
										setSelectedValues([]);
									}}
								/>
							)}
						</FormItemContainer>

						<span>По способу оплаты</span>
						<FormItemContainer>
							<FormItem>
								<Radio.Group
									onChange={handlePaymentMethodChange}
									value={paymentMethod}
								>
									<Radio value={2}>Бонусы</Radio>
									<Radio value={1}>Депозит</Radio>
								</Radio.Group>
							</FormItem>
							{paymentMethod !== null && (
								<DeleteOutlined
									style={{
										fontSize: '20px',
										color: colors.primaryBg,
									}}
									onClick={() => {
										setPaymentMethod(null);
									}}
								/>
							)}
						</FormItemContainer>
					</FormItemsContainer>
					<StyledFooter>
						<StyledButtonSend onClick={onSubmit}>
							<span>Применить фильтр</span>
						</StyledButtonSend>
					</StyledFooter>
				</WrapBoxForm>
			</FormContainer>
		</Wrapper>
	);
};

const Wrapper = styled.div`
	overflow: hidden;
	display: flex;
	position: fixed;
	flex-direction: column;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	z-index: 1000;
	padding: 10px 25px 25px 25px;
	width: 100%;
	min-width: 300px;
	max-width: 430px;
	background: ${colors.bgWhite};
	border-radius: 5px;
`;
const WrapSelect = styled.div`
	align-items: center;
	${flexMixin('column', 'space-between', 'flex-start')};
	width: 100%;
	.ant-select-selection-item-content {
		height: 24px;
		line-height: 22px;
	}
`;

const HeaderTitle = styled.div`
	width: 100%;
	font-family: 'Inter';
	${flexMixin('row', 'space-between', 'flex-end')};
	color: ${colors.grey};
	height: 45px;
`;
const Title = styled.div`
	${textMixin({
		fontSize: '2rem',
		fontWeight: 700,
	})};
	margin-top: 10px;
	line-height: 20px;
	display: flex;
	align-items: flex-end;
`;
const CloseBtn = styled.button`
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	color: #8f8f8f;
	border: none;
	background: ${colors.bgWhite};
	display: flex;
	align-items: flex-start;
	padding-top: 5px;
	cursor: pointer;
	&:hover {
		color: #5e5e5e;
	}
`;
const FormContainer = styled.form`
	${flexMixin('row', 'space-between', 'center')};
	padding: 21px 0px 0px 0px;
	height: 100%;
`;

const WrapBoxForm = styled.div`
	width: 100%;
	${flexMixin('column', 'space-between', 'center')};
	& span {
		font-size: 14px;
		font-weight: 400;
		line-height: 15px;
		color: ${colors.grey};
	}
`;

const StyledFooter = styled.div`
	align-items: center;
	width: 100%;
`;

const StyledButtonSend = styled.button`
	${flexMixin('column', 'center', 'center')};
	cursor: pointer;
	height: 40px;
	margin-top: 15px;
	width: 45%;
	background: ${colors.primaryBg};
	border-radius: 5px;
	border: none;
	width: 100%;
	& span {
		${textMixin({
			fontSize: '1.3rem',
			fontWeight: 600,
		})};
		line-height: 28px;
		text-align: center;
		color: ${colors.cleanWhite};
	}
	&:hover {
		background: #97267d;
	}
`;
const FormItemsContainer = styled.div`
	${flexMixin('column', 'center', 'flex-start')};
	gap: 9px;
	width: 100%;
`;

const FormItemContainer = styled.div`
	${flexMixin('row', 'space-between', 'center')};
	width: 100%;
`;

const FormItem = styled.div`
	width: 90%;
`;

const TimePickerStyled = styled(TimePicker)<{hasError?: boolean}>`
	border-color: ${(props) => (props.hasError ? 'red' : '#d9d9d9')};
	z-index: 1800;
	width: 160px;

	.ant-picker-panel-container {
		width: 160px;

		.ant-picker-time-panel {
			width: 100%;

			.ant-picker-time-panel-column {
				width: 100% !important;
			}
		}
	}

	.ant-picker-now-btn {
		color: ${colors.primaryBg} !important;
	}
	.ant-picker-clear {
		position: absolute;
		right: 0px;
	}

	.ant-picker-suffix {
		position: absolute;
		right: 0px;
	}
`;
